import React from 'react';
import './CalculusOfMeaning.css';

function Pulse() {
  return (
    <div className="calc-divider">
      <div className="calc-line" />
    </div>
  );
}

function GnulChapter6() {
  return (
    <div className="calculus-page">
      {/* Hero */}
      <div className="calculus-hero">
        <div className="calculus-hero-overlay" style={{ background: 'linear-gradient(to bottom, rgba(8,8,8,0.5) 0%, rgba(8,8,8,0.8) 60%, rgba(8,8,8,1) 100%)' }} />
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundImage: 'url(/assets/gnul-hero.png)', backgroundSize: 'cover', backgroundPosition: 'center 20%', zIndex: 1 }} />
        <div className="calculus-hero-content">
          <span className="calculus-hero-label">The Chemistry of Leadership &middot; Chapter 6</span>
          <h1>The Slide Deck</h1>
          <p className="calculus-hero-sub">Voss had 42 slides. Gnul had one question.</p>
        </div>
      </div>

      {/* Content */}
      <div className="calculus-body" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/calculus-bg.png)` }}>
        <div className="calculus-content">

          {/* The Walk */}
          <section className="calc-section reveal">
            <h2>The Walk</h2>
            <p className="calc-lead">
              The office is glass and steel and silence. The kind of building that
              was designed to impress people who have seen buildings before. He has
              not. The lobby alone is larger than the cabin. The ceiling is three
              stories high. A reception desk the size of a boat sits in the center
              with no one behind it because the receptionist does not start until 9
              and Voss demanded everyone arrive at 8.
            </p>
            <p>
              The nine from breakfast walk in behind him. Not with him. Behind him.
              The distinction matters. They are not his entourage. They are people
              who ate eggs with a Half-Orc twenty minutes ago and are now following
              the same hallway because the conference room is at the end of it. But
              the optics are unmistakable. The new CEO walks in first. Nine people
              follow.
            </p>
            <p>
              He can feel the building. The ambient chemistry is different from the
              hotel. The Meridian was engineered for serotonin. This building is
              engineered for productivity. Cool air. Bright lighting. Hard surfaces
              that reflect sound instead of absorbing it. Everything about this space
              says: focus. Perform. Deliver. Nothing about it says: rest. Nothing
              says: you are safe here.
            </p>
            <p>
              The conference room is at the end of the hall. Glass walls. He can see
              inside before he reaches the door. A long table. A screen at the front
              with a slide already loaded. The Æther logo. And standing beside the
              screen, hands clasped behind his back, facing the door, waiting:
            </p>
            <p>
              Caelan Voss.
            </p>
          </section>

          <Pulse />

          {/* Voss */}
          <section className="calc-section reveal">
            <h2>Voss</h2>
            <p>
              He is smaller than Gnul expected. Not short. Compact. The kind of body
              that takes up less space than it controls. Dark suit. No tie. Hair cut
              close. Everything about him is tight. Efficient. Minimized. The physical
              expression of a man who has spent nine years making himself indispensable
              by making himself precise.
            </p>
            <p>
              His face does the thing. The 400-millisecond assessment. But Voss is
              faster than the people in the airport and the hotel and the lobby. His
              recovery takes less than a tenth of a second. His expression does not
              change. His posture does not shift. He looks at Gnul the way you look
              at a variable you have already accounted for.
            </p>
            <p>
              "Mr. Gnul. Welcome to Æther."
            </p>
            <p>
              The voice. Gnul hears it and something in his stomach tightens. Not
              the frequency of the butler. Not the flatness of Aldric Thane. Voss's
              voice is warm. Controlled. Perfectly calibrated. The kind of warmth
              that is produced not by oxytocin but by practice. It has no rough edges.
              No micro-hesitations. No breath in the wrong place. It is the most
              polished frequency he has ever heard and it is completely synthetic.
            </p>
            <p>
              His caretaker's word: <em>bilateral</em>. A voice that reshapes itself
              for the person it is addressing. Last night, Voss sent "report or lose
              your job" to four hundred people. This morning, he is offering a
              handshake with the warmth of a man welcoming a guest to his home.
            </p>
            <p>
              Gnul shakes his hand. Voss's grip is firm. Deliberate. The grip of a
              man who has read the same books about handshakes that Gnul has read
              about vocal frequency. Everything about this man is a performance of
              competence. And it is a good performance. Gnul can see why Seraphine
              trusted him for nine years.
            </p>
            <p>
              "Thank you, Mr. Voss. I appreciate you organizing this."
            </p>
            <p>
              He says it without tone. Without warmth. Without cold. He gives Voss
              nothing to read. Because the moment he gives Voss data, Voss will
              calibrate to it. The way to read a bilateral communicator is to give
              them silence and watch what they produce to fill it.
            </p>
          </section>

          <Pulse />

          {/* The Room */}
          <section className="calc-section reveal">
            <h2>The Room Fills</h2>
            <p>
              They arrive in waves. The local employees first, coming from apartments
              and houses within driving distance. Then the ones from the hotel, in
              groups of three and four, carrying the residue of bad sleep and early
              alarms. Then the remote workers who booked last-minute flights, arriving
              with roller bags and the specific exhaustion of people who slept
              upright in economy seats.
            </p>
            <p>
              The conference room was not designed for this many people. Extra chairs
              are brought in. People stand along the walls. The glass walls of the
              room make it feel like a fishbowl. Everyone can see everyone. Everyone
              can be seen.
            </p>
            <p>
              Gnul counts. Not heads. Chemistry. He watches the room the way his
              caretaker taught him to watch a system. The cortisol is layered. The
              local employees have the lowest baseline. They slept in their own beds.
              The hotel group is mid-range. They got some sleep but not enough. The
              flyers are the worst. Red eyes. Tight jaws. Shoulders locked. They are
              running on chemistry that stopped being functional four hours ago.
            </p>
            <p>
              And all of them are looking at two things. Him. And Voss. Back and
              forth. The new CEO in the back row and the Chief of Staff at the front.
              Two signals. Two frequencies. The room is trying to determine which
              one to orient toward.
            </p>
            <p>
              Gnul sits in the last row. He chose it when he walked in. Voss had
              gestured toward the front, toward a chair beside the screen, the
              co-pilot seat. The seat that says: you are new and I am showing you
              how this works. Gnul walked past it to the back without comment.
            </p>
            <p>
              Voss's jaw tightened for one-tenth of a second. Gnul saw it. The
              micro-expression of a plan being disrupted. Voss had choreographed
              this. The new CEO beside him at the front. The visual of shared
              authority. The message to the room: we are aligned. The CEO sits with
              me because I am the center.
            </p>
            <p>
              Gnul is in the back row. The message is different. The CEO is watching.
            </p>
          </section>

          <Pulse />

          {/* The Deck */}
          <section className="calc-section reveal">
            <h2>42 Slides</h2>
            <p>
              Voss begins. He is good. Gnul gives him that. The voice carries the
              room. The slides are clean. The structure is logical. Organizational
              overview. Revenue targets. Transit corridor metrics. Partnership
              pipeline. Operational efficiency. Headcount allocation.
            </p>
            <p>
              Forty-two slides in thirty-eight minutes. Voss does not rush and he
              does not linger. Every transition is timed. Every data point is
              delivered with the specific cadence of a man who has given this
              presentation before, maybe not this exact one, but this type. The
              type that says: I know everything about this company and you should
              be glad I am here.
            </p>
            <p>
              Gnul watches the room instead of the slides. He watches the chemistry.
            </p>
            <p>
              The first ten minutes: attention. The room is engaged because the room
              is afraid and afraid people listen to the person they believe controls
              their fate. Voss has their cortisol and cortisol produces compliance
              and compliance looks like attention.
            </p>
            <p>
              Minutes ten through twenty: drift. The adrenaline from the threat has
              decayed. The slides are data, not danger. The room begins to lose the
              chemical reason to listen. Eyes glaze. Phones appear under tables.
              The exhausted flyers are fighting to stay awake.
            </p>
            <p>
              Minutes twenty through thirty: resentment. The drift has revealed what
              was underneath the fear all along. They are sitting in a conference room
              they did not need to be in, listening to a presentation that could have
              been an email, delivered by the man who disrupted their lives twelve
              hours ago. The cortisol is gone. The serotonin depletion remains.
              Resentment is a slow chemistry. It does not spike. It settles. It
              lives in the jaw and the crossed arms and the slight lean away from
              the speaker that says: I am here but I have already left.
            </p>
            <p>
              Minutes thirty through thirty-eight: performance. Voss senses the room
              shifting. He accelerates. The slides move faster. His voice gains
              energy. He is compensating with volume for what he has lost in
              chemistry. The room hears it. The increase in energy reads not as
              enthusiasm but as desperation. A speaker who raises their voice when
              the room is pulling away is a speaker who knows they are losing and
              is pushing harder instead of pausing.
            </p>
            <p>
              His caretaker would have paused. She always paused. She said silence
              is the only frequency that invites the listener to come to you instead
              of pushing them away.
            </p>
            <p>
              Voss does not pause. Voss pushes. Voss finishes.
            </p>
            <p>
              Slide 42. "Questions?"
            </p>
          </section>

          <Pulse />

          {/* The Silence */}
          <section className="calc-section reveal">
            <h2>The Silence</h2>
            <p>
              The room does not move. "Questions?" from a man who threatened your job
              twelve hours ago is not an invitation. It is a dare. Every person in
              this room knows that the first hand raised will be the first head
              noticed. And noticed, in Voss's system, is not safe.
            </p>
            <p>
              Three seconds. Five. The silence builds. Voss stands at the front with
              his clicker in his hand and the room stares back at him and nobody
              speaks because the chemistry of the room is telling them: do not be
              seen.
            </p>
            <p>
              Eight seconds.
            </p>
            <p>
              "I have a question."
            </p>
            <p>
              From the back row. Low register. Slow pace. Downward inflection. A
              voice that does not match any frequency the room has heard today. Not
              Voss's polished warmth. Not the tight controlled tones of the anxious
              employees. Something else. Something that sounds like the ground feels.
              Steady.
            </p>
            <p>
              Every head turns. Four hundred people rotate in their seats to look at
              the back row where a Half-Orc in a charcoal suit has spoken for the
              first time in front of the full company.
            </p>
            <p>
              Voss's face does not change. His body does. A micro-shift in weight
              from his right foot to his left. The clicker hand tightens. His chin
              lifts one degree. The posture of a man adjusting for altitude. He did
              not expect the question to come from behind him. He set the room so
              the question would come from the front, from the seats facing him,
              where the asker would be looking up. The back row inverts that. Gnul
              is looking across the room, not up at it. And the room has turned to
              face him, which means they have turned their backs to Voss.
            </p>
            <p>
              "Of course. Please."
            </p>
            <p>
              Voss's voice is perfect. Warm. Inviting. Not a crack. But the "please"
              is new. Voss did not say "please" in any of the 42 slides. "Please"
              is the word that appears when a bilateral communicator recalibrates
              upward. He has decided, in this moment, that Gnul is above him.
            </p>
          </section>

          <Pulse />

          {/* The Question */}
          <section className="calc-section reveal">
            <h2>The Question</h2>
            <p>
              Gnul does not stand. He speaks from the chair. The same position he
              used in the hotel lobby. Seated. Unhurried. Not performing authority.
              Simply occupying space.
            </p>
            <p>
              "When you sent the message last night requiring everyone to be here
              this morning, did you consider how it would affect their cortisol?"
            </p>
            <p>
              The room stops breathing.
            </p>
            <p>
              The word. Cortisol. It sits in the air like a foreign object. This is
              a corporate conference room. People say "morale" in conference rooms.
              They say "engagement." They say "culture." They do not say cortisol.
              They do not name the molecule.
            </p>
            <p>
              Voss blinks. One blink. The first involuntary response Gnul has seen
              from him. The blink of a man who received an input he has no script
              for.
            </p>
            <p>
              "I'm sorry?"
            </p>
            <p>
              "Cortisol. The stress hormone. Half-life of ninety minutes. You sent
              the message at approximately 9 PM. The people in this room who had to
              book flights experienced an immediate adrenaline spike followed by
              sustained cortisol elevation through the booking process, the travel,
              the arrival at a hotel they did not plan to be in. By the time they
              walked into this room, the adrenaline had cleared but the cortisol
              residue had converted to resentment, which is serotonin depletion,
              which does not clear in ninety minutes. It takes days."
            </p>
            <p>
              The room is not breathing. Four hundred people are watching a man
              they made memes about describe their exact internal experience of the
              last twelve hours with clinical precision. Every person who booked a
              red-eye, who scrambled for childcare, who sat in an economy seat with
              their jaw locked and their stomach tight, is hearing someone name what
              happened inside their body as though he was there. As though he felt it
              too.
            </p>
            <p>
              He did feel it. In the airport. In the terminal. On the plane. He
              knows what their chemistry did because his did the same thing, except
              his was caused by four hundred strangers' faces and theirs was caused
              by one man's message.
            </p>
            <p>
              "You did not give them time to prepare. You did not give them a reason
              that justified the disruption. You gave them a threat and a deadline.
              The threat activated cortisol. The deadline activated adrenaline. And
              the combination of the two, applied to people who were already at home,
              already off-shift, already in their parasympathetic state, produced
              exactly what it produces in every nervous system that has ever received
              a sudden threat with no escape route: compliance driven by fear."
            </p>
            <p>
              He pauses. Not for effect. Because his caretaker taught him that the
              pause after a hard truth is where the listener's prefrontal cortex
              catches up to what their amygdala already knows.
            </p>
            <p>
              "My question is simple. Did you consider that? Before you sent it?"
            </p>
          </section>

          <Pulse />

          {/* Voss */}
          <section className="calc-section reveal">
            <h2>The Answer</h2>
            <p>
              Voss stands at the front of the room. The clicker is still in his
              hand. His posture has not changed. His face has not changed. He is
              good. He is very good. Nine years of navigating Seraphine Vael, who
              built a company that crosses galaxies, have made Caelan Voss one of
              the most composed human beings Gnul has ever observed.
            </p>
            <p>
              But the room has changed. The room has turned. Not physically. They
              turned physically when Gnul spoke. What has changed now is the
              chemistry. The ambient signal has shifted from Voss to Gnul. The
              attention that cortisol held for Voss, the compliance that fear
              produced, has been redirected by a single question that named what
              every person in the room felt and could not articulate.
            </p>
            <p>
              Voss speaks.
            </p>
            <p>
              "The decision to convene was made in the interest of organizational
              alignment during a leadership transition. I understand the short
              notice was inconvenient, and I appreciate everyone's flexibility."
            </p>
            <p>
              Corporate. Perfect. A sentence constructed from words that mean
              nothing arranged in a way that sounds like everything. "Organizational
              alignment." "Leadership transition." "Appreciate everyone's flexibility."
              Every phrase is a shield. Every word is chosen to deflect rather than
              engage.
            </p>
            <p>
              Gnul listens to the frequency, not the words. The warmth is still
              there but it has thinned. The register is the same but the pace has
              increased by a fraction. Voss is not panicking. Voss does not panic.
              But he is recalculating. The question was not in his model. The CEO
              who sits in the back row and asks about cortisol was not in his model.
              The model had a new CEO who would sit in the front, nod along, and
              let Voss continue to run the company the way Voss has been running
              the company.
            </p>
            <p>
              The model is broken.
            </p>
            <p>
              Gnul nods. Once. The nod that says: I heard you. Not the nod that
              says: I accept your answer.
            </p>
            <p>
              "Thank you, Mr. Voss."
            </p>
            <p>
              He does not follow up. He does not challenge. He does not argue. He
              asked one question. He received one answer. The answer told the room
              everything it needed to know. Not because of what Voss said. Because
              of what Voss did not say.
            </p>
            <p>
              Voss did not say: you are right, I should have handled that
              differently. Voss did not say: I did not consider the impact. Voss
              did not say anything that acknowledged the humanity of the four
              hundred people he disrupted. He said "organizational alignment" and
              "appreciate your flexibility" and every person in the room heard the
              gap between what Gnul asked and what Voss answered.
            </p>
            <p>
              The gap is the lesson. The gap is where trust dies. When a leader is
              asked a human question and gives a corporate answer, the nervous
              system of every person listening files it as: this person does not
              see me. This person sees the organization. I am not a person to this
              person. I am a resource being aligned.
            </p>
          </section>

          <Pulse />

          {/* After */}
          <section className="calc-section reveal">
            <h2>After</h2>
            <p>
              The meeting ends. Voss dismisses the room. People stand. The sound of
              four hundred chairs pushing back on hard floors. The murmur begins
              immediately. Low. Urgent. The frequency of people processing what
              just happened.
            </p>
            <p>
              Gnul remains seated. He does not stand until the room is half empty.
              He is not avoiding people. He is giving them space. A crowd funneling
              through a door will not stop to talk to the CEO. But a person walking
              past a man sitting alone in the back row might.
            </p>
            <p>
              The woman in the gray blazer stops.
            </p>
            <p>
              "That was the first time anyone has ever said the word 'cortisol'
              in this building."
            </p>
            <p>
              He looks up at her. "It will not be the last."
            </p>
            <p>
              She almost smiles. Not quite. The chemistry is not there yet. But
              the direction is right. The vector has changed. Yesterday this woman
              did not know if she was losing her job. This morning she had breakfast
              with her CEO. And just now she watched that CEO ask the man who
              threatened her a question that named what she felt and could not say.
            </p>
            <p>
              That is the beginning. Not of loyalty. Of attention. She is watching
              now. They all are. Not with fear. With curiosity.
            </p>
            <p>
              Dopamine. The molecule of "what happens next."
            </p>
            <p>
              The room empties. Voss remains at the front. Gnul remains at the back.
              The screen still shows slide 42. "Questions?" The irony of it sits in
              the air between them.
            </p>
            <p>
              Voss speaks first. His voice is different now. The warmth is gone. Not
              replaced with cold. Replaced with nothing. The frequency of a man who
              has decided to stop performing.
            </p>
            <p>
              "That was an interesting approach."
            </p>
            <p>
              "Thank you."
            </p>
            <p>
              "I have been running this company for eight months."
            </p>
            <p>
              "I know."
            </p>
            <p>
              "Seraphine trusted me."
            </p>
            <p>
              "I know that too."
            </p>
            <p>
              Voss looks at him. Not the practiced look. Not the composed assessment.
              He looks at Gnul the way a person looks at another person when the
              masks are down and the conference room is empty and there is no
              audience to perform for. It is the first honest frequency Voss has
              produced.
            </p>
            <p>
              "I do not know what you are. But I know what this company needs. And
              it is not a biochemistry lecture."
            </p>
            <p>
              Gnul stands. The full height. He does not do it to intimidate. He does
              it because he has been sitting for forty-five minutes and his body
              needs to move and he is done making himself small for this man.
            </p>
            <p>
              "You are right. This company does not need a biochemistry lecture."
            </p>
            <p>
              He picks up his phone. He puts it in his pocket. He walks toward the
              door.
            </p>
            <p>
              "It needs a biochemist."
            </p>
            <p>
              He does not look back. He walks out of the conference room, down the
              glass hallway, past the empty reception desk, into a morning that is
              brighter than the one he walked in from.
            </p>
            <p>
              His phone buzzes. Loriann.
            </p>
            <p>
              <em>"The entire company Slack just went silent. What did you do?"</em>
            </p>
            <p>
              He types:
            </p>
            <p>
              <em>"I asked a question."</em>
            </p>
          </section>

          <Pulse />

          {/* The Science */}
          <section className="calc-section reveal">
            <h2>The Science Behind Chapter 6</h2>

            <div className="calc-equation gold">
              <span className="calc-eq-label">The Diagnostic Question</span>
              <code>if question names the listener's internal state &rarr; listener feels seen &rarr; OT spike + trust formation<br />if answer deflects with corporate language &rarr; listener feels unseen &rarr; trust erosion</code>
              <span className="calc-eq-desc">A question that names what people feel but cannot articulate produces an oxytocin response in every listener. The person asking becomes associated with understanding. If the person answering deflects with corporate language, the contrast between the two signals accelerates trust erosion for the deflector and trust formation for the asker. Gnul did not attack Voss. He made Voss's own answer do the work.</span>
            </div>

            <div className="calc-equation">
              <span className="calc-eq-label">Attention Decay in Fear-Based Audiences</span>
              <code>t=0: cortisol-driven attention (high)<br />t=10min: adrenaline decay &rarr; attention drops<br />t=20min: resentment onset &rarr; active disengagement<br />t=30min: speaker compensates with energy &rarr; reads as desperation</code>
              <span className="calc-eq-desc">A fear-based audience follows a predictable attention curve. Initial compliance driven by cortisol decays within minutes as the adrenaline clears. What replaces it is not boredom but resentment, an active withdrawal of engagement. A speaker who raises energy to compensate triggers a secondary assessment: why is this person pushing harder? The audience reads the increase as insecurity, not passion. Voss lost the room by minute twenty. He did not know because compliance looks like attention.</span>
            </div>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Seating Position as Power Architecture</span>
              <code>front row beside speaker = subordinate alignment<br />back row alone = observer authority<br />room turns to face back row = power inversion</code>
              <span className="calc-eq-desc">Voss choreographed the seating to place Gnul beside him at the front: the visual of shared authority with Voss as the anchor. Gnul's move to the back row inverted the architecture. When he spoke, the room turned physically to face him, turning their backs to Voss. The power structure of the room was rewritten by a seating choice made before a word was spoken.</span>
            </div>

            <div className="calc-equation">
              <span className="calc-eq-label">Corporate Language as Trust Signal</span>
              <code>human question + corporate answer = signal mismatch &rarr; trust assessment: inauthentic</code>
              <span className="calc-eq-desc">"Did you consider their cortisol?" is a human question. "Organizational alignment during a leadership transition" is a corporate answer. When the register of the answer does not match the register of the question, every listener's nervous system flags the mismatch. The speaker is assessed as inauthentic not because they lied but because they refused to meet the question where it was asked. The deflection IS the data.</span>
            </div>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Curiosity Replaces Fear</span>
              <code>if fear(Voss) decays AND novel_signal(Gnul) persists &rarr; DA replaces cortisol &rarr; "what happens next?"</code>
              <span className="calc-eq-desc">The room entered in fear of Voss. It exited in curiosity about Gnul. The transition is pharmacokinetic: cortisol has a half-life of 90 minutes, so fear decays on a predictable curve. Dopamine, triggered by the prediction error of a CEO who asks about cortisol from the back row, has no such decay while the stimulus remains novel. Fear fades. Curiosity compounds. By the time the room empties, the dominant chemistry is no longer "will I lose my job?" It is "what will he do next?" The calculus of curiosity has displaced the calculus of harm.</span>
            </div>
          </section>

          <Pulse />

          {/* Navigation */}
          <section className="calc-section reveal" style={{ textAlign: 'center', padding: '2rem 0' }}>
            <a href="/gnul/chapter-5" style={{ color: '#c9a84c', textDecoration: 'none', fontSize: '1.1rem' }}>&larr; Chapter 5: The Lobby</a>
            <span style={{ color: '#333', margin: '0 2rem' }}>|</span>
            <a href="/gnul/chapter-7" style={{ color: '#c9a84c', textDecoration: 'none', fontSize: '1.1rem' }}>Chapter 7: The Bill &rarr;</a>
          </section>

          {/* Signature */}
          <div className="calc-signature reveal">
            <p><strong>Nexus Concordat Inc.</strong></p>
            <p>Narrative Physics&trade; Leadership Development</p>
            <div className="patent-tags">
              <span className="patent-tag">Patent Pending: 63/939,190</span>
              <span className="patent-tag">Patent Pending: 63/962,385</span>
              <span className="patent-tag">Patent Pending: 63/988,485</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default GnulChapter6;
